import { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import RotatingLinesLoader from './components/_shared/Loaders/RotatingLinesLoader'
import PageNotFound from './modules/pageNotFound'
import AppListReportsTable from './modules/adminTools/pages/reports/Pages/AppListReport/index'
import WmrcAvgResponseReport from './modules/adminTools/pages/reports/Pages/WmrcAvgResponseTimeReport/index'
import CommentReqReport from './modules/adminTools/pages/reports/Pages/CommentReqReport/index'
import IncidentCountWmrcGroupReport from './modules/adminTools/pages/reports/Pages/IncidentCountWmrcGroupReport/index'
import OpenIncidentWmrcReport from './modules/adminTools/pages/reports/Pages/OpenIncidentsWmrcReport/index'
import CurrentUserReport from './modules/adminTools/pages/reports/Pages/CurrentUserReport/index'
import GetNewUserReport from './modules/adminTools/pages/reports/Pages/GetNewUserReport/index'
import IncidentAnalysis from './modules/adminTools/pages/reports/Pages/IncidentAnalysis/index'
import IncidentProcessedByInitialReview from './modules/adminTools/pages/reports/Pages/IncidentProcessedByInitialReview/index'
import IncidentProcessedByWmrc from './modules/adminTools/pages/reports/Pages/IncidentProcessedByWmrc/index'
import InitialReviewsByCongregation from './modules/adminTools/pages/reports/Pages/initialReviewsByCongregation/index'
import LocalIncidentsProcessedByWmrc from './modules/adminTools/pages/reports/Pages/LocalIncidentsProcessedByWmrc/index'
import ScoreAdjustmentAnalysis from './modules/adminTools/pages/reports/Pages/ScoreAdjustmentAnalysis/index'
import SpecificAppUsersReport from './modules/adminTools/pages/reports/Pages/SpecificAppUsersReport/index'
import SystemReport from './modules/adminTools/pages/reports/Pages/SytemReport/index'
import UrlsVsIncident from './modules/adminTools/pages/reports/Pages/UrlsVsIncident/index'
import UserList from './modules/adminTools/pages/reports/Pages/UserList/index'
import WmrcResponseTime from './modules/adminTools/pages/reports/Pages/WmrcResponseTime/index'
import MyIncidentsListing from './modules/incidents/Pages/myIncidentsListing'
import CustomizedErrorBoundary from './components/_shared/CustomizedErrorBoundary/CustomizedErrorBoundary'
import DeviceHighScoringReport from './modules/adminTools/pages/reports/Pages/DeviceHighScoringReport/DeviceHighScoringReport'

const CloseMultipleIncidents = lazy(() =>
    import('./modules/adminTools/pages/closeMutlipleIncidents')
)
const NewAppList = lazy(() => import('./modules/apps/pages/NewAppList'))
const AppInformation = lazy(() => import('./modules/appInformation'))

const CategoryListing = lazy(() =>
    import('./modules/adminTools/pages/categoriesListing')
)
const SystemRulesListing = lazy(() =>
    import('./modules/adminTools/pages/systemRules/systemRulesListing/index')
)
const EditUserGroup = lazy(() =>
    import('./modules/adminTools/pages/userGroups/editUserGroup')
)
const CreateUserGroup = lazy(() =>
    import('./modules/adminTools/pages/userGroups/createUserGroup')
) 
const CreateUserDistrict = lazy(() =>
    import('./modules/adminTools/pages/userGroups/userDistrict/createUserDistrict')
)
const EditUserDistrict = lazy(() =>
    import('./modules/adminTools/pages/userGroups/userDistrict/editUserDistrict')
)
const UserDistrict = lazy(() =>
    import('./modules/adminTools/pages/userGroups/userDistrictListing/index')
)
const CloneSystemRule = lazy(() =>
    import(
        './modules/adminTools/pages/systemRules/CloneSystemRule/CloneSystemRule'
    )
)
const EditAppGroup = lazy(() =>
    import('./modules/apps/pages/AppGroupList/editAppGroup/EditAppGroup')
)
const AddAppGroup = lazy(() =>
    import('./modules/apps/pages/AppGroupList/addAppGroup/AddAppGroup')
)
const CategorizeNewApp = lazy(() =>
    import('./modules/apps/pages/categorizeNewApp/CategorizeNewApp')
)
const AssignAppToSpecificUser = lazy(() =>
    import(
        './modules/apps/pages/assignAppToSpecificUser/AssignAppToSpecificUser'
    )
)
const RefLevelListing = lazy(() =>
    import('./modules/systemSettings/Pages/refLevel/refLevelListing')
)
const RefCategoryLevelListing = lazy(() =>
    import(
        './modules/systemSettings/Pages/refCategoryLevel/refCategoryLevelListing'
    )
)
const PermissionsValidator = lazy(() =>
    import('./hoc/PermissionsValidator/PermissionsValidator')
)
const EditRefLevel = lazy(() =>
    import('./modules/systemSettings/Pages/refLevel/editRefLevel')
)
const EditRefCategoryLevel = lazy(() =>
    import(
        './modules/systemSettings/Pages/refCategoryLevel/editRefCategoryLevel'
    )
)
const ScoreAdjustmentListing = lazy(() =>
    import(
        './modules/systemSettings/Pages/scoreAdjustmentReasons/scoreAdjusmentListing'
    )
)
const QuickScoreAdjustmentListing = lazy(() =>
    import(
        './modules/systemSettings/Pages/quickScoreAdjustment/quickScoreAdjustmentListing'
    )
)
const EditScoreAdjustment = lazy(() =>
    import(
        './modules/systemSettings/Pages/scoreAdjustmentReasons/editScoreAdjustment'
    )
)
const EditQuickScoreAdjustment = lazy(() =>
    import(
        './modules/systemSettings/Pages/quickScoreAdjustment/editQuickScoreAdjustment'
    )
)
const CreateScoreAdjustment = lazy(() =>
    import(
        './modules/systemSettings/Pages/scoreAdjustmentReasons/createScoreAdjustment'
    )
)
const CreateQuickScoreAdjustment = lazy(() =>
    import(
        './modules/systemSettings/Pages/quickScoreAdjustment/createQuickScoreAdjustment'
    )
)
const ContactTypeListing = lazy(() =>
    import('./modules/systemSettings/Pages/contactTypes/contactTypeListing')
)
const EditContactType = lazy(() =>
    import('./modules/systemSettings/Pages/contactTypes/editContactType')
)
const HeaderQuickLinksListing = lazy(() =>
    import(
        './modules/systemSettings/Pages/headerQuickLinks/headerQuickLinksListing'
    )
)
const CreateHeaderQuickLinks = lazy(() =>
    import(
        './modules/systemSettings/Pages/headerQuickLinks/createHeaderQuickLinks'
    )
)
const EditHeaderQuickLinks = lazy(() =>
    import(
        './modules/systemSettings/Pages/headerQuickLinks/editHeaderQuickLinks'
    )
)
const InstallerSourceListing = lazy(() =>
    import('./modules/adminTools/pages/installerSource/InstallerSourceListing')
)

const Scoring = lazy(() => import('./modules/adminTools/pages/scoring'))
const ScoringWhitelist = lazy(() =>
    import('./modules/adminTools/pages/scoringWhitelist')
)
const ScoringWhitelistBrowser = lazy(() =>
    import('./modules/adminTools/pages/scoringWhitelistBrowser')
)

const DeviceHighScoringListing = lazy(() =>
    import(
        './modules/adminTools/pages/devicesHighScoring/DevicesHighScoringListing'
    )
)
const EditAppCategory = lazy(() =>
    import('./modules/adminTools/pages/appCategories/editappCategories')
)
const CreateAppCategory = lazy(() =>
    import('./modules/adminTools/pages/appCategories/createappCategories')
)

const ManageAppCategories = lazy(() =>
    import(
        './modules/adminTools/pages/appCategories/appCategoriesListing/index'
    )
)
const Reports = lazy(() =>
    import('./modules/adminTools/pages/reports/Pages/Reports')
)
const AlternateIncident = lazy(() =>
    import('./modules/adminTools/pages/reports/Pages/AlternateIncident/index')
)
const ReportWrapper = lazy(() =>
    import('./modules/adminTools/pages/reports/index')
)

const ContactsWrapper = lazy(() => import('./modules/contacts/index'))
const Contact = lazy(() => import('./modules/contacts/pages/contactListing'))
const ContactDetails = lazy(() =>
    import('./modules/contacts/pages/editContact')
)
const NewUserContact = lazy(() =>
    import('./modules/contacts/pages/createContact/index')
)
const AppListWrapper = lazy(() => import('./modules/apps'))
const EditAppList = lazy(() =>
    import('./modules/apps/pages/EditAppList/EditAppList')
)
const AppList = lazy(() => import('./modules/apps/pages/AppList/index'))
const Dashboard = lazy(() => import('./modules/dashboard'))
const UserWrapper = lazy(() => import('./modules/user'))
const User = lazy(() => import('./modules/user/Pages/userListing'))

const UserDetail = lazy(() => import('./modules/user/Pages/editUser'))
const NewUserDetail = lazy(() => import('./modules/user/Pages/newUser'))
const Incidents = lazy(() => import('./modules/incidents'))
const Devices = lazy(() => import('./modules/devices'))
const UserDevices = lazy(() =>
    import('./modules/devices/Pages/userDeviceListing')
)
const EditDevice = lazy(() => import('./modules/devices/Pages/editDevice'))
const LocalIncidents = lazy(() =>
    import('./modules/incidents/Pages/localIncidentListing')
)
const EditLocalIncidents = lazy(() =>
    import('./modules/incidents/Pages/editLocalIncident/EditLocalIncidents')
)
const EditIncidentGuard = lazy(() =>
    import('./modules/incidents/Pages/editLocalIncident/EditIncidentGuard')
)
const UnreviewedFlags = lazy(() => import('./modules/unreviewedFlags'))
const UsageRecords = lazy(() => import('./modules/usageRecords'))
const Roles = lazy(() => import('./modules/adminTools/pages/rolesList'))
const AppGroupList = lazy(() =>
    import('./modules/apps/pages/AppGroupList/AppGroupList')
)
const AppCategories = lazy(() => import('./modules/apps/pages/AppCategories'))
const EditUnacceptableAppIncidentSetting = lazy(() =>
    import('./modules/apps/pages/EditUnacceptableAppIncident')
)
const EditSetting = lazy(() =>
    import('./modules/apps/pages/editAppNotificationEmailSetting')
)
const AppsUsageLength = lazy(() =>
    import('./modules/apps/pages/appsUsageLength')
)
const AdminTools = lazy(() => import('./modules/adminTools'))
const EmailSettingList = lazy(() =>
    import('./modules/adminTools/pages/emailSettings/emailSettingListing/index')
)

const EmailHistoryList = lazy(() =>
    import('./modules/adminTools/pages/emailSettings/emailHistoryListing/index')
)

const EmailEditTemplate = lazy(() =>
    import('./modules/adminTools/pages/emailSettings/EmailEditTemplate/index')
)
 
const SMSHistory = lazy(()=> import('./modules/adminTools/pages/smsHistory/index'))

const UserGroups = lazy(() =>
    import('./modules/adminTools/pages/userGroups/userGroupsListing')
)
const EditSystemRule = lazy(() =>
    import('./modules/adminTools/pages/systemRules/EditSystemRule')
)
const NewSystemRule = lazy(() =>
    import('./modules/adminTools/pages/systemRules/NewSystemRule')
)
const CheckRule = lazy(() =>
    import('./modules/adminTools/pages/systemRules/CheckRule/index')
)
const UsageAffectedRule = lazy(() =>
    import('./modules/adminTools/pages/systemRules/UsageAffectedRule')
)

const SystemSetting = lazy(() => import('./modules/systemSettings'))

const SettingListing = lazy(() =>
    import('./modules/systemSettings/Pages/settingListing')
)
const EditSystemSetting = lazy(() =>
    import('./modules/systemSettings/Pages/editSetting')
)
const Layout = lazy(() => import('../app/hoc/Layout/Layout'))
const UserAssignedAppList = lazy(() =>
    import('./modules/apps/pages/userAssignedAppList')
)
const EditRoleModal = lazy(() =>
    import('./modules/adminTools/pages/rolesList/Modal/EditRoleModal')
)
const CongreationDefaultListing = lazy(() =>
    import(
        './modules/adminTools/pages/congregationDefaults/congregationDefaultsListing'
    )
)

const WMRCDivisionListing = lazy(() =>
    import(
        './modules/adminTools/pages/wmrcDivision/wmrcDivisionListing/WMRCDivisionListing'
    )
)

const CommentsOnIncident = lazy(()=> import('./modules/commentsOnIncident/index'))

const CheckApps = lazy(() => import('./modules/checkApps/index'))
const MyAccount = lazy(() => import('./modules/myAccount'))

const DeviceCount = lazy(() => import('./modules/DeviceCountPage/DeviceCountPage'))
const TotalDevice = lazy(()=> import('./modules/confirmDeviceCount/ConfirmDeviceCount'))

const PrivatePage = () => {
    return (
        <Routes>
            <Route
                element={
                    <Suspense fallback={<RotatingLinesLoader />}>
                        <Layout />
                    </Suspense>
                }
            >
                <Route
                    path="/"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <CustomizedErrorBoundary key={`dashboard`}>
                                <PermissionsValidator
                                    module="dashboard"
                                    capabilities={['list']}
                                >
                                    <Dashboard />
                                </PermissionsValidator>
                            </CustomizedErrorBoundary>
                        </Suspense>
                    }
                />
                <Route
                    path="/"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <UserWrapper />
                        </Suspense>
                    }
                >
                    <Route
                        path="users"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="users"
                                    capabilities={['list']}
                                >
                                    <User />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="users/editUser/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="users"
                                    capabilities={['view']}
                                >
                                    <UserDetail />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="users/newUser"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="users"
                                    capabilities={['create']}
                                >
                                    <NewUserDetail />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                </Route>
                <Route
                    path="/incidents"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <Incidents />
                        </Suspense>
                    }
                >
                    <Route
                        path="localincidents"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <CustomizedErrorBoundary key={'localincidents'}>
                                    <PermissionsValidator
                                        module="incidents"
                                        capabilities={['list']}
                                    >
                                        <LocalIncidents />
                                    </PermissionsValidator>
                                </CustomizedErrorBoundary>
                            </Suspense>
                        }
                    />
                    <Route
                        path="editincident/:incident_id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <EditIncidentGuard />
                            </Suspense>
                        }
                    />
                </Route>
                <Route
                    path="/myincidents"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>

                            <Incidents />
                        </Suspense>
                    }
                >
                    <Route
                        path="myincidents"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="myincidents"
                                    capabilities={['list']}
                                >
                                    <MyIncidentsListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="editmyincident/:incident_id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="myincidents"
                                    capabilities={['view']}
                                >
                                    <EditLocalIncidents />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                </Route>

                <Route
                    path="/unreviewedflags"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <PermissionsValidator
                                module="unreviewedflags"
                                capabilities={['list']}
                            >
                                <UnreviewedFlags />
                            </PermissionsValidator>
                        </Suspense>
                    }
                ></Route>
                <Route
                    path="/usagerecords"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <PermissionsValidator
                                module="usage_record"
                                capabilities={['list']}
                            >
                                <UsageRecords />
                            </PermissionsValidator>
                        </Suspense>
                    }
                ></Route>
                <Route
                    path="/contacts"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <ContactsWrapper />
                        </Suspense>
                    }
                >
                    <Route
                        path=""
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="contacts"
                                    capabilities={['list']}
                                >
                                    <Contact />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="createnewusercontact"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="contacts"
                                    capabilities={['store']}
                                >
                                    <NewUserContact />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="editContacts/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <CustomizedErrorBoundary key={'editcontact'}>
                                    <PermissionsValidator
                                        module="contacts"
                                        capabilities={['view']}
                                    >
                                        <ContactDetails />
                                    </PermissionsValidator>
                                </CustomizedErrorBoundary>
                            </Suspense>
                        }
                    />
                </Route>
                <Route
                    path="/appInformation"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <PermissionsValidator
                                module="apps"
                                capabilities={['list']}
                            >
                                <AppInformation />
                            </PermissionsValidator>
                        </Suspense>
                    }
                />
                <Route
                    path="/appcategories"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <PermissionsValidator
                                module="app_category"
                                capabilities={['list']}
                            >
                                <AppCategories />
                            </PermissionsValidator>
                        </Suspense>
                    }
                />
                <Route
                    path="/devices"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <Devices />
                        </Suspense>
                    }
                >
                    <Route
                        path="searchDevices"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <CustomizedErrorBoundary
                                    key={`userdevicese-module`}
                                >
                                    <PermissionsValidator
                                        module="devices"
                                        capabilities={['list']}
                                    >
                                        <UserDevices />
                                    </PermissionsValidator>
                                </CustomizedErrorBoundary>
                            </Suspense>
                        }
                    />
                    <Route
                        path="editDevice/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="devices"
                                    capabilities={['view']}
                                >
                                    <EditDevice />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                </Route>
                <Route
                    path="/apps"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <AppListWrapper />
                        </Suspense>
                    }
                >
                    <Route
                        path="new_app_list"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="apps"
                                    capabilities={['list']}
                                >
                                    <NewAppList />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path=""
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="apps"
                                    capabilities={['list']}
                                >
                                    <AppList />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="editapps/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="apps"
                                    capabilities={[ 'view']}
                                >
                                    <EditAppList />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="categorize_new_app/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="apps"
                                    capabilities={['categorize_app']}
                                >
                                    <CategorizeNewApp />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="edit_app_list_for_specific_user/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <AssignAppToSpecificUser />
                            </Suspense>
                        }
                    />
                    <Route
                        path="appgrouplist"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="app_group"
                                    capabilities={['list']}
                                >
                                    <AppGroupList />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                     
                    <Route
                        path="editappgroup/:id"
                        element={
                            <PermissionsValidator
                                module="app_group"
                                capabilities={['view']}
                            >
                                <EditAppGroup />
                            </PermissionsValidator>
                        }
                    />
                    <Route
                        path="newappgroup"
                        element={
                            <PermissionsValidator
                                module="app_group"
                                capabilities={['store']}
                            >
                                <AddAppGroup />
                            </PermissionsValidator>
                        }
                    />
                    <Route
                        path="blacklistappsettings/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <EditUnacceptableAppIncidentSetting />
                            </Suspense>
                        }
                    />
                    <Route
                        path="editSetting/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                
                                <EditSetting />
                            </Suspense>
                        }
                    />
                    <Route
                        path="userassignedapplist"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="users_assigned_app_list"
                                    capabilities={["list"]}
                                >
                                    <UserAssignedAppList />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="usagelength"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="apps"
                                    capabilities={["app_usage_length"]}
                                >
                                    <AppsUsageLength />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                </Route>
               
                <Route
                    path="/admintools"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <AdminTools />
                        </Suspense>
                    }
                >
                    <Route
                        path="manage_app_categories"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="app_category"
                                    capabilities={['list']}
                                >
                                    <AppCategories />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="usergroups"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="groups"
                                    capabilities={['list']}
                                >
                                    <UserGroups />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                     <Route
                    path="roles"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <PermissionsValidator
                                module="roles"
                                capabilities={['list']}
                            >
                                <Roles />
                            </PermissionsValidator>
                        </Suspense>
                    }
                />
                    <Route
                        path="roles/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="roles"
                                    capabilities={['view', 'update']}
                                >
                                    <EditRoleModal />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="emailTemplates"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="email_templates"
                                    capabilities={['list']}
                                >
                                    <EmailSettingList />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                      {/* EmailHistoryList */}
                    <Route
                        path="emailHistory"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <EmailHistoryList />
                            </Suspense>
                        }
                    />
                    <Route
                        path="emailTemplates/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <EmailEditTemplate />
                            </Suspense>
                        }
                    />
                    {/* sms history */}
                    <Route
                        path="smsHistory"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <SMSHistory />
                            </Suspense>
                        }
                    />
                     <Route
                    path="roles"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <PermissionsValidator
                                module="roles"
                                capabilities={['list']}
                            >
                                <Roles />
                            </PermissionsValidator>
                        </Suspense>
                    }
                />
                    <Route
                        path="apps/categories"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <ManageAppCategories />
                            </Suspense>
                        }
                    />
                    <Route
                        path="apps/edit/categories/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                            
                            

                                <EditAppCategory />
                       </Suspense>
                        }
                    />
                    <Route
                        path="apps/createcategory"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                 <PermissionsValidator
                                    module="app_category"
                                    capabilities={['store']}
                                >
                                <CreateAppCategory />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="usergroups/createusergroups"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="groups"
                                    capabilities={['create']}
                                >
                                    <CreateUserGroup />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                     <Route
                        path="usergroups/userdistrict"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                    <UserDistrict />
                            </Suspense>
                        }
                    />
                     <Route
                        path="usergroups/userdistrict/createuserdistrict"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="groups"
                                    capabilities={['create']}
                                >
                                    <CreateUserDistrict />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="usergroups/userdistrict/edit/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="groups"
                                    capabilities={['view']}
                                >
                                    <EditUserDistrict />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="usergroups/editusergroups/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="groups"
                                    capabilities={['view']}
                                >
                                    <EditUserGroup />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="categories"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="categories"
                                    capabilities={['list']}
                                >
                                    <CategoryListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="rules"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="rules"
                                    capabilities={['list']}
                                >
                                    <SystemRulesListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />

                    <Route
                        path="rules/editRule/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="rules"
                                    capabilities={['view']}
                                >
                                    <EditSystemRule />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="rules/newRule"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="rules"
                                    capabilities={['store']}
                                >
                                    <NewSystemRule />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="rules/checkrule"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <CheckRule />
                            </Suspense>
                        }
                    />
                    <Route
                        path="rules/clonerule"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="rules"
                                    capabilities={['clone']}
                                >
                                    <CloneSystemRule />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="rules/affected-events/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <UsageAffectedRule />
                            </Suspense>
                        }
                    />
                    <Route
                        path="installersource"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="installer_source"
                                    capabilities={['list']}
                                >
                                    <InstallerSourceListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="scoring"
                        element={
                             <Suspense fallback={<RotatingLinesLoader />}>
                                 <PermissionsValidator module="scoring_dashboard" capabilities={['list']}>
                            <Scoring />
                                </PermissionsValidator>
                             </Suspense>
                        }
                    />
                    <Route
                        path="scoring-whitelist"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="scoring_dashboard"
                                    capabilities={['list']}
                                >
                                    <ScoringWhitelist />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="scoring-whitelist-browser"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="scoring_dashboard"
                                    capabilities={['list']}
                                >
                                    <ScoringWhitelistBrowser />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="devicehighscoring"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="device_high_scoring"
                                    capabilities={['list']}
                                >
                                    <DeviceHighScoringListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="/admintools/closemultipleIncidents"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="close_multiple_incidents"
                                    capabilities={['list']}
                                >
                                    <CloseMultipleIncidents />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    {/* Congregation Defaults route removed - functionality moved to User Groups tabs */}
                    {/* <Route
                        path="groups/congregationDefault"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="congregation_defaults"
                                    capabilities={['list']}
                                >
                                    <CongreationDefaultListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    /> */}
                    <Route
                        path="groups/congregations/districtHierarchy"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <WMRCDivisionListing />
                            </Suspense>
                        }
                    />
                </Route>
                <Route
                      path="/updatedevicecount/:id?"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                 <PermissionsValidator
                                        module="device_count_page"
                                        capabilities={['list']}
                                    >

                                  <DeviceCount />
                                    </PermissionsValidator>
                            </Suspense>
                        }
                    />
                     <Route
                      
                      path="/confirmDeviceCount"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                    <TotalDevice />
                            </Suspense>
                        }
                    />
                <Route
                    path="/settings"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <SystemSetting />
                        </Suspense>
                    }
                >
                    <Route
                        path="systemsettings"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <CustomizedErrorBoundary
                                    key={'system_setting_list'}
                                >
                                    <PermissionsValidator
                                        module="system_settings"
                                        capabilities={['list']}
                                    >
                                        <SettingListing />
                                    </PermissionsValidator>
                                </CustomizedErrorBoundary>
                            </Suspense>
                        }
                    />
                    <Route
                        path="systemsettings/editSetting/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <CustomizedErrorBoundary
                                    key={`edit_system_setting`}
                                >
                                    <PermissionsValidator
                                        module="system_settings"
                                        capabilities={['view']}
                                    >
                                        <EditSystemSetting />
                                    </PermissionsValidator>
                                </CustomizedErrorBoundary>
                            </Suspense>
                        }
                    />
                    <Route
                        path="refLevels"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="ref_levels"
                                    capabilities={['list']}
                                >
                                    <RefLevelListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="refLevels/editrefLevels/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="ref_levels"
                                    capabilities={['view']}
                                >
                                    <EditRefLevel />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="refcategorylevels"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="ref_category_levels"
                                    capabilities={['list']}
                                >
                                    <RefCategoryLevelListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="refcategorylevels/editrefcategorylevels/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="ref_category_levels"
                                    capabilities={['view']}
                                >
                                    <EditRefCategoryLevel />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="scoreadjustmentreasons"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="score_adjustment_reasons"
                                    capabilities={['list']}
                                >
                                    <ScoreAdjustmentListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="scoreadjustmentreasons/editscoreadjustmentreasons/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module="score_adjustment_reasons"
                                    capabilities={['view']}
                                >
                                    <EditScoreAdjustment />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="scoreadjustmentreasons/createcoreadjustmentreasons"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`score_adjustment_reasons`}
                                    capabilities={['store']}
                                >
                                    <CreateScoreAdjustment />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="scoreAdjustmentFlags"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`quick_score_adjustment`}
                                    capabilities={[`list`]}
                                >
                                    <QuickScoreAdjustmentListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="scoreAdjustmentFlags/editScoreAdjustmentFlags/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`quick_score_adjustment`}
                                    capabilities={[`view`]}
                                >
                                    <EditQuickScoreAdjustment />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="scoreAdjustmentFlags/createScoreAdjustmentFlags"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`quick_score_adjustment`}
                                    capabilities={[`store`]}
                                >
                                    <CreateQuickScoreAdjustment />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="contacttypes"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`contact_types`}
                                    capabilities={[`list`]}
                                >
                                    <ContactTypeListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="contacttypes/editcontacttype/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`contact_types`}
                                    capabilities={[`view`, `update`]}
                                >
                                    <EditContactType />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="headerquicklinks"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`header_quick_links`}
                                    capabilities={[`list`]}
                                >
                                    <HeaderQuickLinksListing />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="headerquicklinks/createheaderquicklinks"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`header_quick_links`}
                                    capabilities={[`store`]}
                                >
                                    <CreateHeaderQuickLinks />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="headerquicklinks/editheaderquicklinks/:id"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`header_quick_links`}
                                    capabilities={[`view`]}
                                >
                                    <EditHeaderQuickLinks />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                </Route>
                <Route
                    path="/reports"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <PermissionsValidator
                                module={`reports`}
                                capabilities={[`list`]}
                            >
                                <ReportWrapper />
                            </PermissionsValidator>
                        </Suspense>
                    }
                >
                    <Route
                        path=""
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <Reports />
                            </Suspense>
                        }
                    />
                    <Route
                        path="alternateIncidentNotification"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[
                                        `view_alternate_incident_notification`,
                                    ]}
                                >
                                    <AlternateIncident />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="currentUserReport"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[
                                        `view_district_group_user_report`,
                                    ]}
                                >
                                    <CurrentUserReport />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="appListReport"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_app_list_report`]}
                                >
                                    <AppListReportsTable />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="GetNewUserReport"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_new_user_report`]}
                                >
                                    <GetNewUserReport />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="SystemReport"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_system_report`]}
                                >
                                    <SystemReport />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="IncidentProcessedByInitialReview"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[
                                        `view_incidents_processed_by_initial_review`,
                                    ]}
                                >
                                    <IncidentProcessedByInitialReview />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="LocalIncidentsProcessedByWmrc"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[
                                        `view_local_incidents_processed_by_wmrc`,
                                    ]}
                                >
                                    <LocalIncidentsProcessedByWmrc />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="InitialReviewsByCongregation"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[
                                        `view_initial_reviews_by_congregation`,
                                    ]}
                                >
                                    <InitialReviewsByCongregation />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="LocalReviewsByCongregation"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[
                                        `view_local_reviews_by_congregation`,
                                    ]}
                                >
                                    <InitialReviewsByCongregation />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="ScoreAdjustmentAnalysis"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[
                                        `view_score_change_analysis`,
                                    ]}
                                >
                                    <ScoreAdjustmentAnalysis />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="WmrcResponseTime"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_wmrc_response_times`]}
                                >
                                    <WmrcResponseTime />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="UserList"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_user_list`]}
                                >
                                    <UserList />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="IncidentProcessedByWmrc"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[
                                        `view_incidents_processed_by_wmrc`,
                                    ]}
                                >
                                    <IncidentProcessedByWmrc />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="SpecificAppUsersReport"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[
                                        `view_specific_app_users_report`,
                                    ]}
                                >
                                    <SpecificAppUsersReport />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="IncidentAnalysis"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_incident_analysis`]}
                                >
                                    <IncidentAnalysis />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                     <Route
                        path="WmrcAvgResponseTime"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_wmrc_avg_response_time`]}
                                >
                                   <WmrcAvgResponseReport />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                     <Route
                        path="commentReqReport"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_comment_request`]}
                                >
                                   <CommentReqReport />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                     <Route
                        path="incidentCountWmrcGroup"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_incident_count_wmrc_group`]}
                                >
                                   <IncidentCountWmrcGroupReport />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                     <Route
                        path="openIncidentWmrc"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_open_incidents_wmrc`]}
                                >
                                   <OpenIncidentWmrcReport />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                    <Route
                        path="UrlsVsIncident"
                        element={
                            <Suspense fallback={<RotatingLinesLoader />}>
                                <PermissionsValidator
                                    module={`reports`}
                                    capabilities={[`view_urls_vs_incidents`]}
                                >
                                    <UrlsVsIncident />
                                </PermissionsValidator>
                            </Suspense>
                        }
                    />
                </Route>
                <Route
                    path="/appSearch"
                    element={
                        <PermissionsValidator
                            module={`check_apps`}
                            capabilities={[`list`]}
                        >
                            <CheckApps />
                        </PermissionsValidator>
                    }
                />
                <Route
                    path="/myaccount"
                    element={
                        <PermissionsValidator
                            module={`my_account`}
                            capabilities={['view']}
                        >
                            <MyAccount />
                        </PermissionsValidator>
                    }
                />
                <Route
                    path="/commentsOnIncident"
                    element={
                        <Suspense fallback={<RotatingLinesLoader />}>
                            <CommentsOnIncident />
                        </Suspense>
                    }
                ></Route>
                    <Route path="/reports/deviceHighScore" element={<DeviceHighScoringReport />} />
                {/* <Route path="/sitecheck" element={<UserCheck />} /> */}
                <Route path="*" element={<PageNotFound />} />
            </Route>
        </Routes>
    )
}

export default PrivatePage
